import {
    Badge, Button, CircularProgress, Grid, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Tooltip, Typography, useTheme
} from "@mui/material";
import {
    IconFilter,
    IconSortDescending2,
    IconSortAscending2,
    IconChartCandle
} from "@tabler/icons-react";
import { getPager } from "helper/common";
import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import MainCard from "ui-component/cards/MainCard";
import SubCard from "ui-component/cards/SubCard";
import { NoData } from "ui-component/custom-components/nodata";
import { VWPagination } from "ui-component/custom-components/pagination";
import { ErrorAlert } from "ui-component/custom-components/alertMessage";
import GenerateReport from "./generateReport";
import FilterReportGeneration from "./fitler";
import FieldLists from "./fieldsList";
import moment from "moment";
import {
    clearReportGenerationList,
    closeFieldsTableDialog,
    closeReportFilterDialog,
    closeReportGenerationCreateDialog,
    getReportGenerationList,
    openFieldsTableDialog,
    openReportFilterDialog,
    openReportGenerationCreateDialog,
    reportGenerationData,
    reportGenerationListErrorClear,
    setCurrentBirdsRecord,
    setReportFilteredData
} from "store/app/report-management/report-generations/actions";
import Chip from "ui-component/extended/Chip";

const tableHeaderStyle = { paddingLeft: '0px' };

const TruncatedHeaderCell = ({ type, children }) => {
    const ref = useRef(null);
    const [showTooltip, setShowTooltip] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;

        const check = () => {
            setShowTooltip(el.scrollWidth > el.clientWidth);
        };

        check();

        const observer = new ResizeObserver(check);
        observer.observe(el);

        return () => observer.disconnect();
    }, [children]);

    return (
        <TableCell sx={{ maxWidth: type === "header" ? 200 : 350 }}>
            <Tooltip title={showTooltip ? children : ''} arrow placement="top">
                <Typography
                    ref={ref}
                    variant={type === "header" ? "h5" : "inherit"}
                    noWrap
                    sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        cursor: showTooltip ? "pointer" : ""
                    }}
                >
                    {children}
                </Typography>
            </Tooltip>
        </TableCell>
    );
};

const ReportGenerationList = (props) => {
    //--------------------List--------------------------------------
    const { reportGenerationList, totalCounts, isPending, errorMessage } = props;

    const [pageSize, setPageSize] = useState(10);
    const [Pager, setPager] = useState({ currentPage: 1 });
    const [sortDirection, setSortDirection] = useState('asc');
    const [sortOrder, setSortOrder] = useState(null);

    //--------------------Generation Data---------------------------
    const { isReportGenerationCreateDialogOpen, reportData } = props;
    //--------------------Filter------------------------------------
    const { isReportFilterDialogOpen, reportFilterData } = props;
    //--------------------Fields------------------------------------
    const { isFieldsTableDialogOpen, birdRecord } = props;

    //--------------------UI Design---------------------------------
    const theme = useTheme();
    const colorPrimary = theme.palette.secondary.dark;
    const [tableHeight, setTableHeight] = useState(window.innerHeight);

    useEffect(() => {
        props.reportGenerationListErrorClear();

        const handleResize = () => {
            setTableHeight(window.innerHeight)
        };
        window.addEventListener("resize", handleResize);
        return () => {
            props.clearReportGenerationList();
            // props.reportGenerationData();
            window.removeEventListener("resize", handleResize);
        }
    }, [])

    const handleSort = (column) => {
        if (sortOrder === column) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        }
        else {
            setSortOrder(column);
            setSortDirection('asc');
        }
    };

    const setPage = (page = 1) => {
        if (reportData) {
            props.getReportGenerationList(page, pageSize, sortOrder, sortDirection, reportData, reportFilterData);
        }
        setPager(prevPager => {
            const totalPages = Math.ceil(totalCounts / pageSize);
            const visiblePages = 4;
            const pageOffset = Math.max(0, Math.floor((page - 1) / visiblePages) * visiblePages);
            const newPages = [];
            for (let i = pageOffset + 1; i <= Math.min(totalPages, pageOffset + visiblePages); i++) {
                newPages.push(i);
            }
            return {
                ...prevPager,
                currentPage: page,
                totalPages,
                pages: newPages,
            }
        })
    }

    useEffect(() => {
        const currentPager = getPager(totalCounts, 1, pageSize);
        setPager(currentPager)
    }, [totalCounts]);

    useEffect(() => {
        setPage();
    }, [reportFilterData, reportData, sortOrder, sortDirection, pageSize])

    const handleFilterOpen = () => {
        props.openReportFilterDialog();
    }
    const handleFilterClose = () => {
        props.closeReportFilterDialog();
    }

    const handleGenerateReportOpen = () => {
        props.openReportGenerationCreateDialog();
    }
    const handleGenerateReportClose = () => {
        props.closeReportGenerationCreateDialog();
    }

    const handleFieldsTableOpen = (e, fields, farmName, reportName, reportValue) => {
        props.setCurrentBirdsRecord(fields, farmName, reportName, reportValue);
        props.openFieldsTableDialog();
    }
    const handleFieldsTableClose = () => {
        props.closeFieldsTableDialog();
    }

    return (
        <>
            <SubCard sx={{
                p: 1.5,
                pl: 2,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}
            >
                <Grid item sx={{ display: 'flex', gap: { lg: 2, md: 2, sm: 1, xs: 1 }, alignItems: "center", marginRight: "10px" }}>
                    <Typography variant="h4">Reports Generation List - </Typography>
                    <Tooltip title="Reports Generation Count" placement="top">
                        <span>
                            <Chip
                                label={totalCounts}
                                chipcolor="secondary"
                                size="small"
                                sx={{
                                    borderRadius: "20%",
                                    py: 1,
                                    fontWeight: "bold",
                                    fontSize: 15,
                                    cursor: "pointer",
                                }}
                            />
                        </span>
                    </Tooltip>
                </Grid>
                <Grid item sx={{ display: 'flex', gap: 2 }}>
                    <Badge
                        badgeContent={reportFilterData ? Object.keys(reportFilterData).length : 0}
                        color="secondary"
                        invisible={reportFilterData ? Object.keys(reportFilterData).length === 0 : true}
                    >
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleFilterOpen}
                            startIcon={<IconFilter />}
                        >
                            Filter
                        </Button>
                    </Badge>
                    <Badge
                        // badgeContent={reportData?.length}
                        anchorOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                        variant="dot"
                        color="secondary"
                        invisible={!reportData}
                    >
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handleGenerateReportOpen}
                            startIcon={<IconChartCandle />}
                        >
                            Generate Report
                        </Button>
                    </Badge>
                </Grid>
            </SubCard>
            <MainCard sx={{ marginTop: '15px' }}>
                {errorMessage && <ErrorAlert type={'error'} content={errorMessage} />}
                <TableContainer sx={{ whiteSpace: "nowrap", maxHeight: `calc(${tableHeight}px - 380px)`, overflow: "auto" }}>
                    <Table size="small" stickyHeader aria-label="sticky table"
                        sx={{
                            '& tr > *:last-child': {
                                position: 'sticky',
                                right: 0,
                                bgcolor: 'white',
                            },
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    <Button sx={tableHeaderStyle}
                                        onClick={() => handleSort('RecordsIndex')}
                                        endIcon={sortOrder === 'RecordsIndex' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        Farm Record No.
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Button sx={tableHeaderStyle}
                                        onClick={() => handleSort('FarmName')}
                                        endIcon={sortOrder === 'FarmName' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        Farm Name
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Button
                                        sx={tableHeaderStyle}
                                        onClick={() => handleSort('InspectionDate')}
                                        endIcon={sortOrder === 'InspectionDate' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        Inspection Date
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Button
                                        sx={tableHeaderStyle}
                                        onClick={() => handleSort('Location')}
                                        endIcon={sortOrder === 'Location' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        Location
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Button
                                        sx={tableHeaderStyle}
                                        onClick={() => handleSort('RegionName')}
                                        endIcon={sortOrder === 'RegionName' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        Region Name
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Button
                                        sx={tableHeaderStyle}
                                        onClick={() => handleSort('State')}
                                        endIcon={sortOrder === 'State' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        State/Export
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Button
                                        sx={tableHeaderStyle}
                                        onClick={() => handleSort('District')}
                                        endIcon={sortOrder === 'District' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        District
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Button
                                        sx={tableHeaderStyle}
                                        onClick={() => handleSort('ExaminedBirdsCount')}
                                        endIcon={sortOrder === 'ExaminedBirdsCount' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        Examined Birds Count
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Button
                                        sx={tableHeaderStyle}
                                        onClick={() => handleSort('TotalNoOfBirdsPlaced')}
                                        endIcon={sortOrder === 'TotalNoOfBirdsPlaced' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        Total No Of Birds Placed
                                    </Button>
                                </TableCell>
                                <TableCell>
                                    <Typography color="primary" variant="button">
                                        Fields
                                    </Typography>
                                </TableCell>
                                <TableCell sx={{ color: theme.typography.button }}>
                                    <Typography color="primary" variant="button">
                                        Formula Name
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography color="primary" variant="button">
                                        Formula
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Button
                                        sx={tableHeaderStyle}
                                        onClick={() => handleSort('ReportValue')}
                                        endIcon={sortOrder === 'ReportValue' && (
                                            sortDirection === 'asc' ?
                                                <IconSortAscending2 color={colorPrimary} />
                                                : <IconSortDescending2 color={colorPrimary} />
                                        )}
                                    >
                                        Report Value
                                    </Button>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(reportGenerationList?.length > 0) && !isPending ?
                                reportGenerationList?.map((data, index) => {
                                    return <TableRow key={index}>
                                        <TableCell>{data.RecordsIndex || 'N/A'}</TableCell>
                                        <TableCell>{data.FarmName || 'N/A'}</TableCell>
                                        <TableCell>{data.InspectionDate ? moment(data.InspectionDate).format("DD MMM YYYY") : 'N/A'}</TableCell>
                                        <TruncatedHeaderCell type="body">{data.Location || 'N/A'}</TruncatedHeaderCell>
                                        <TableCell>{data.RegionName || 'N/A'}</TableCell>
                                        <TableCell>{data.State || 'N/A'}</TableCell>
                                        <TableCell>{data.District || 'N/A'}</TableCell>
                                        <TableCell>{data.ExaminedBirdsCount || '0'}</TableCell>
                                        <TableCell>{data.TotalNoOfBirdsPlaced || '0'}</TableCell>
                                        <TableCell>
                                            <Button
                                                onClick={(e) => handleFieldsTableOpen(e, data.BirdsRecord, data.FarmName, data.ReportName, data.ReportValue)}
                                                size="small"
                                                sx={{ p: 0 }}
                                                color="secondary"
                                            // startIcon={<IconSearch />}
                                            >
                                                Show Fields
                                            </Button>
                                        </TableCell>
                                        <TableCell>{data.ReportName || '0'}</TableCell>
                                        <TableCell>{reportData?.formula || '0'}</TableCell>
                                        <TableCell>{data.ReportValue || '0'}</TableCell>

                                    </TableRow>
                                })
                                : isPending ?
                                    <TableRow >
                                        <TableCell colSpan={12} height='300px'>
                                            <Grid container alignItems={"center"} justifyContent={"center"} gap={1}
                                                sx={{
                                                    position: "sticky",
                                                    width: "fit-content",
                                                    top: "50%",
                                                    left: "50%",
                                                    transform: "translate(-50%, -50%)"
                                                }}
                                            >
                                                <CircularProgress />
                                                <Typography variant="h5" color={'primary'}> Loading...</Typography>
                                            </Grid>
                                        </TableCell>
                                    </TableRow>
                                    : <TableCell colSpan={12} height='300px'>
                                        <NoData />
                                    </TableCell>
                            }
                        </TableBody>
                    </Table>
                </TableContainer>
                {(totalCounts > 0) &&
                    <Grid>
                        <VWPagination
                            pageDetails={Pager}
                            pageChange={setPage}
                            totalRecords={totalCounts}
                            pageSize={pageSize}
                            setPageSize={setPageSize}
                        />
                    </Grid>
                }
            </MainCard>
            <br />
            <GenerateReport
                open={isReportGenerationCreateDialogOpen}
                onClose={handleGenerateReportClose}
                setPage={setPage}
                totalRecords={totalCounts}
                pageSize={pageSize}
                setPageSize={setPageSize}
            />
            <FilterReportGeneration
                open={isReportFilterDialogOpen}
                onClose={handleFilterClose}
            />
            <FieldLists
                open={isFieldsTableDialogOpen}
                fields={birdRecord}
                onClose={handleFieldsTableClose}
            />
        </>
    )
}

const mapStateToProps = (state) => ({
    //--------------------List--------------------------------------
    reportGenerationList: state.reportGeneration.reportGenerationList,
    totalCounts: state.reportGeneration.reportGenerationListCount,
    isPending: state.reportGeneration.isReportGenerationListPending,
    errorMessage: state.reportGeneration.reportGenerationListErrorMessage,
    //--------------------Generation Data---------------------------
    reportData: state.reportGeneration.reportGeneratedData,
    isReportGenerationCreateDialogOpen: state.reportGeneration.isReportGenerationCreateDialogOpen,
    //--------------------Filter------------------------------------
    isReportFilterDialogOpen: state.reportGeneration.isReportFilterDialogOpen,
    reportFilterData: state.reportGeneration.reportFilterData,
    //--------------------Fields------------------------------------
    isFieldsTableDialogOpen: state.reportGeneration.isFieldsTableDialogOpen,
    birdRecord: state.reportGeneration.birdRecord,
})

const mapDispatchToProps = (dispatch) => ({
    //--------------------List--------------------------------------
    getReportGenerationList: (page, pageSize, sortOrder, sortDirection, reportData, filters) => dispatch(getReportGenerationList(page, pageSize, sortOrder, sortDirection, reportData, filters)),
    clearReportGenerationList: () => dispatch(clearReportGenerationList()),
    reportGenerationListErrorClear: () => dispatch(reportGenerationListErrorClear()),
    //--------------------Generation Data---------------------------
    openReportGenerationCreateDialog: () => dispatch(openReportGenerationCreateDialog()),
    closeReportGenerationCreateDialog: () => dispatch(closeReportGenerationCreateDialog()),
    reportGenerationData: () => dispatch(reportGenerationData()),
    //--------------------Filter------------------------------------
    openReportFilterDialog: () => dispatch(openReportFilterDialog()),
    closeReportFilterDialog: () => dispatch(closeReportFilterDialog()),
    setReportFilteredData: (filter) => dispatch(setReportFilteredData(filter)),
    //--------------------Fields------------------------------------
    openFieldsTableDialog: () => dispatch(openFieldsTableDialog()),
    closeFieldsTableDialog: () => dispatch(closeFieldsTableDialog()),
    setCurrentBirdsRecord: (birdRecord, farmName, reportName, reportValue) => dispatch(setCurrentBirdsRecord(birdRecord, farmName, reportName, reportValue)),
})
export default connect(mapStateToProps, mapDispatchToProps)(ReportGenerationList);